import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIDefinitionsPlugin } from '../../aiDefinitionsPluginType';
import type { AIDefinitionsPluginConfig } from '../../types';
import { isLivePageViewMode } from '../../ui/utils/api';

import { debouncedUpdateKeywordsAndDecorations } from './commands';
import { createPluginState, getPluginState } from './plugin-factory';
import { aiDefinitionsPluginKey } from './plugin-key';
import { getInitialState } from './state';
import { onClickKeywordDecoration } from './util/keyword';
import {
	onMouseoutKeywordStyler,
	onMouseoverKeywordStyler,
	setupKeywordStylerMutationObserver,
	updateAllKeywordStylerPositions,
} from './util/keyword-styler';

export const createAIDefinitionsPMPlugin = (
	{ dispatch }: PMPluginFactoryParams,
	{
		api,
		config,
	}: {
		// api is mandatory but can be undefined
		api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
		config: AIDefinitionsPluginConfig;
	},
) => {
	return new SafePlugin({
		key: aiDefinitionsPluginKey,

		state: createPluginState(dispatch, () => getInitialState()),

		view: (editorView: EditorView) => {
			if (!config.autoHighlightEnabled) {
				return {};
			}

			if (fg('platform_editor_ai_definitions_pluginstateeffect')) {
				return {
					update: (editorView: EditorView, prevState: EditorState) => {
						const { keywordRegex, keywordDecorationsSessionCounter } = getPluginState(
							editorView.state,
						);
						const prevPluginState = getPluginState(prevState);

						// if it's view mode, and there is no keywordRegex, and it's a new session, update keywords and decorations
						if (
							api?.editorViewMode.sharedState.currentState()?.mode === 'view' &&
							!keywordRegex &&
							keywordDecorationsSessionCounter !== prevPluginState.keywordDecorationsSessionCounter
						) {
							debouncedUpdateKeywordsAndDecorations({ api, config, editorView });
						}
					},
				};
			} else {
				// currently, only show auto-highlights in view mode
				// write it separately so that we can easily remove the condition if we want to support edit mode in the future
				if (!isLivePageViewMode({ api })) {
					return {};
				}

				// when the decorations aren't changed,
				// the styler DOM nodes are already inserted when `view` is called on Hello (but not locally)
				// so there must be time racing here, but not sure if all nodes are ready
				// so always run the mutation observer to make sure all styler DOM nodes will be positioned correctly

				updateAllKeywordStylerPositions();

				const destroyKeywordStylerMutationObserver = setupKeywordStylerMutationObserver(
					editorView.dom,
				);

				return {
					update: (_: EditorView, prevState: EditorState) => {
						const { keywordRegex, keywordDecorationsSessionCounter } = getPluginState(
							editorView.state,
						);
						const { keywordDecorationsSessionCounter: prevKeywordDecorationsSessionCounter } =
							getPluginState(prevState);
						// if it's view mode, and there is no keywordRegex, and it's a new session, update keywords and decorations
						if (
							isLivePageViewMode({ api }) &&
							!keywordRegex &&
							keywordDecorationsSessionCounter !== prevKeywordDecorationsSessionCounter
						) {
							debouncedUpdateKeywordsAndDecorations({ api, config, editorView });
						}
					},
					destroy: () => {
						destroyKeywordStylerMutationObserver();
					},
				};
			}
		},

		props: {
			decorations(state: EditorState) {
				const { keywordDecorations } = getPluginState(state);

				if (fg('platform_editor_ai_definitions_pluginstateeffect')) {
					// currently, only show auto-highlights in view mode
					if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
						return keywordDecorations;
					}
				} else {
					// currently, only show auto-highlights in view mode
					if (isLivePageViewMode({ api })) {
						return keywordDecorations;
					}
				}

				return DecorationSet.empty;
			},

			// currently, only show auto-highlights in view mode
			// so we only need to handle DOM events in view mode
			// reading mode outside of callbacks is not accurate, so need to read it in callbacks
			handleDOMEvents: fg('platform_editor_ai_definitions_pluginstateeffect')
				? {
						mouseover: (editorView: EditorView, event: MouseEvent) => {
							if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
								onMouseoverKeywordStyler(event);
							}
						},

						mouseout: (editorView: EditorView, event: MouseEvent) => {
							if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
								onMouseoutKeywordStyler(event);
							}
						},

						click: (editorView: EditorView, event: MouseEvent) => {
							if (api?.editorViewMode.sharedState.currentState()?.mode === 'view') {
								onClickKeywordDecoration({ editorView, event, api });
							}
						},
					}
				: isLivePageViewMode({ api })
					? {
							mouseover: (editorView: EditorView, event: MouseEvent) => {
								onMouseoverKeywordStyler(event);
							},

							mouseout: (editorView: EditorView, event: MouseEvent) => {
								onMouseoutKeywordStyler(event);
							},

							click: (editorView: EditorView, event: MouseEvent) => {
								onClickKeywordDecoration({ editorView, event, api });
							},
						}
					: undefined,
		},
	});
};
